import { GetStaticProps, GetStaticPropsContext } from 'next';
import { useTranslation } from 'next-i18next';
import { i18n } from 'next-i18next.config';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import React from 'react';
import { LayoutType } from 'src/components/layout/layout';
import Box, { HeaderTheme, HeadingElement } from 'src/components/ui-kit/box/box';
import { configuration } from 'src/configurations/configuration';
import { wrapper } from 'src/store';
import styles from './privacy-policy.module.scss';

const PrivacyPolicyPage = (): JSX.Element => {
  const { t } = useTranslation(['components:pages:privacy-policy']);

  return (
    <>
      <Head>
        <title>US Mineral Exchange | Privacy Policy</title>
        <link
          rel='canonical'
          href={`${process.env.NEXT_PUBLIC_URL}/privacy-policy`} />
      </Head>
      <Box
        heading={t('TEXT_PRIVACY_POLICY')}
        headingTheme={HeaderTheme.DARK}
        headingElement={HeadingElement.H1}>
        <div className={styles.privacypolicy}>
          <p>
            This Privacy Policy governs the manner in which US Mineral Exchange, LLC collects, uses, maintains and
            discloses information collected from users (each, a &quot;User&quot;) of the&nbsp;
            <span className={styles.privacypolicy_link}>www.usmineralexchange.com</span>
            &nbsp;website (&quot;Site&quot;). This privacy policy applies to the Site and all products and services
            offered by US Mineral Exchange, LLC.
          </p>
          <h3>Personal identification information</h3>
          <p>
            We may collect personal identification information from Users in a variety of ways, including, but not
            limited to, when Users visit our site, register on the site, place an order, subscribe to the newsletter,
            respond to a survey, fill out a form, and in connection with other activities, services, features or
            resources we make available on our Site. Users may be asked for, as appropriate, name, email address,
            mailing address, phone number, credit card information. Users may, however, visit our Site anonymously. We
            will collect personal identification information from Users only if they voluntarily submit such information
            to us. Users can always refuse to supply personally identification information, except that it may prevent
            them from engaging in certain Site related activities.
          </p>
          <h3>Non-personal identification information</h3>
          <p>
            We may collect non-personal identification information about Users whenever they interact with our Site.
            Non-personal identification information may include the browser name, the type of computer and technical
            information about Users means of connection to our Site, such as the operating system and the Internet
            service providers utilized and other similar information.
          </p>
          <h3>Web browser cookies</h3>
          <p>
            Our Site may use &quot;cookies&quot; to enhance User experience. User&apos;s web browser places cookies on
            their hard drive for record-keeping purposes and sometimes to track information about them. User may choose
            to set their web browser to refuse cookies, or to alert you when cookies are being sent. If they do so, note
            that some parts of the Site may not function properly.
          </p>
          <h3>How we use collected information</h3>
          <p>US Mineral Exchange, LLC may collect and use Users personal information for the following purposes:</p>
          <ul className={styles.privacypolicy_list}>
            <li>
              <em>To improve customer service</em>
              <p>
                Information you provide helps us respond to your customer service requests and support needs more
                efficiently.
              </p>
            </li>
            <li>
              <em>To personalize user experience</em>
              <p>
                We may use information in the aggregate to understand how our Users as a group use the services and
                resources provided on our Site.
              </p>
            </li>
            <li>
              <em>To improve our Site</em>
              <p>We may use feedback you provide to improve our products and services.</p>
            </li>
            <li>
              <em>To run a promotion, contest, survey or other Site feature</em>
              <p>To send Users information they agreed to receive about topics we think will be of interest to them.</p>
            </li>
            <li>
              <em>To send periodic emails</em>
              <p>
                We may use the email address to send User information and updates pertaining to their order. It may also
                be used to respond to their inquiries, questions, and/or other requests. If User decides to opt-in to
                our mailing list, they will receive emails that may include company news, updates, related product or
                service information, etc. If at any time the User would like to unsubscribe from receiving future
                emails, they may do so by contacting us via our Site.
              </p>
            </li>
          </ul>
          <h3>How we protect your information</h3>
          <div>
            We adopt appropriate data collection, storage and processing practices and security measures to protect
            against unauthorized access, alteration, disclosure or destruction of your personal information, username,
            password, transaction information and data stored on our Site.
            <p>
              Sensitive and private data exchange between the Site and its Users happens over a SSL secured
              communication channel and is encrypted and protected with digital signatures.
            </p>
            <h3>Sharing your personal information</h3>
            <p>
              We do not sell, trade, or rent Users personal identification information to others. We may share generic
              aggregated demographic information not linked to any personal identification information regarding
              visitors and users with our business partners, trusted affiliates and advertisers for the purposes
              outlined above.We may use third party service providers to help us operate our business and the Site or
              administer activities on our behalf, such as sending out newsletters or surveys. We may share your
              information with these third parties for those limited purposes provided that you have given us your
              permission.
            </p>
            <h3>Third party websites</h3>
            <p>
              Users may find advertising or other content on our Site that link to the sites and services of our
              partners, suppliers, advertisers, sponsors, licensors and other third parties. We do not control the
              content or links that appear on these sites and are not responsible for the practices employed by websites
              linked to or from our Site. In addition, these sites or services, including their content and links, may
              be constantly changing. These sites and services may have their own privacy policies and customer service
              policies. Browsing and interaction on any other website, including websites which have a link to our Site,
              is subject to that website&apos;s own terms and policies.
            </p>
            <h3>Advertising</h3>
            <p>
              Ads appearing on our site may be delivered to Users by advertising partners, who may set cookies. These
              cookies allow the ad server to recognize your computer each time they send you an online advertisement to
              compile non personal identification information about you or others who use your computer. This
              information allows ad networks to, among other things, deliver targeted advertisements that they believe
              will be of most interest to you. This privacy policy does not cover the use of cookies by any advertisers.
            </p>
            <h3>Our Advertising Partners</h3>
            <p>
              Some of the ads may be served by Google. Google&apos;s use of the DART cookie enables it to serve ads to
              Users based on their visit to our Site and other sites on the Internet. DART uses &quot;non personally
              identifiable information&quot; and does NOT track personal information about you, such as your name, email
              address, physical address, etc. You may opt out of the use of the DART cookie by visiting the Google ad
              and content network privacy policy at&nbsp;
              <a
                href='https://policies.google.com/technologies/ads'
                target='_blank'
                className={styles.privacypolicy_link}
                rel='noreferrer'
              >
                <u>https://policies.google.com/technologies/ads</u>
              </a>
            </p>
            <h3>Compliance with children&apos;s online privacy protection act</h3>
            <p>
              Protecting the privacy of the very young is especially important. For that reason, we never collect or
              maintain information at our Site from those we actually know are under 13, and no part of our website is
              structured to attract anyone under 13.
            </p>
            <h3>Changes to this privacy policy</h3>
            <p>
              US Mineral Exchange, LLC has the discretion to update this privacy policy at any time. When we do, we will
              revise the updated date at the bottom of this page. We encourage Users to frequently check this page for
              any changes to stay informed about how we are helping to protect the personal information we collect. You
              acknowledge and agree that it is your responsibility to review this privacy policy periodically and become
              aware of modifications.
            </p>
            <h3>Your acceptance of these terms</h3>
            <p>
              By using this Site, you signify your acceptance of this policy. If you do not agree to this policy, please
              do not use our Site. Your continued use of the Site following the posting of changes to this policy will
              be deemed your acceptance of those changes.
            </p>
            <h3>Contacting us</h3>
            <p>
              If you have any questions about this Privacy Policy, the practices of this site, or your dealings with
              this site, please contact us at:
            </p>
            <p>
              <span className={styles.privacypolicy_link}>US Mineral Exchange, LLC</span>
              <br />
              <span className={styles.privacypolicy_link}>www.usmineralexchange.com</span>
            </p>
            <address>
              <p>U.S. Mineral Exchange LLC, 2448 East 81st Street, Suite 2046, Tulsa, Oklahoma 74137</p>
            </address>
            <p>
              This document was last updated on <time>March 12, 2023</time>
            </p>
          </div>
        </div>
      </Box>
    </>
  );
};

PrivacyPolicyPage.layout = LayoutType.ACCOUNT;

export default PrivacyPolicyPage;

export const getStaticProps: GetStaticProps = wrapper.getStaticProps(
  () => async ({ locale, preview }: GetStaticPropsContext) => {
    if (preview) {
      return {
        redirect: {
          permanent: false,
          destination: configuration.redirectPaths.checkPreview
        }
      };
    }

    return {
      props: {
        ...(await serverSideTranslations(locale ?? i18n.defaultLocale, [
          'common',
          'components:layout',
          'components:pages:privacy-policy'
        ]))
      },
      revalidate: configuration.revalidation.timeSec
    };
  }
);
